const clean = value => String(value ?? '').replace(/\s+/g, ' ').trim();
const parse = (root, selector) => { try { return JSON.parse(root.querySelector(selector)?.textContent || 'null'); } catch { return null; } };
const unique = values => [...new Set(values)];
const safeURL = value => {
  if (!value || (typeof value !== 'string' && !(value instanceof URL))) return null;
  try { const url = new URL(value, location.origin); return url.origin === location.origin ? url : null; } catch { return null; }
};
const safeImage = value => {
  if (typeof value !== 'string' || !value.trim()) return '';
  try { const url = new URL(value, location.origin); return ['http:', 'https:'].includes(url.protocol) ? url.href : ''; } catch { return ''; }
};
const pieceFor = value => /\b(bottom|pants|trousers|shorts|skirt)\b/i.test(value || '') ? 'bottom' : /\b(top|jacket|hoodie)\b/i.test(value || '') ? 'top' : '';
const isSeparate = product => {
  const pieces = (product.options || []).flatMap(option => option.values || []).map(pieceFor);
  return pieces.includes('top') && pieces.includes('bottom');
};
const selectionColour = product => product.colorIndex >= 0 ? product.selected?.options?.[product.colorIndex] || '' : '';
const colourFamily = value => {
  const text = clean(value).replace(/\([^)]*\)/g, '').toLowerCase();
  if (/black|nero|noir|schwarz/.test(text)) return 'black';
  if (/white|ivory|cream|bianco|blanc/.test(text)) return 'white';
  if (/grey|gray|silver|charcoal|grigio/.test(text)) return 'grey';
  if (/brown|coffee|khaki|beige|camel|tan|marrone/.test(text)) return 'earth';
  if (/blue|navy|indigo|blu/.test(text)) return 'blue';
  if (/red|wine|burgundy|rosso/.test(text)) return 'red';
  if (/green|olive|verde/.test(text)) return 'green';
  return text;
};
function category(product, colour = selectionColour(product)) {
  if (isSeparate(product)) return pieceFor(colour) === 'bottom' ? 'bottom' : 'outer';
  const classify = text => {
    text = String(text || '').toLowerCase();
    if (/\b(jeans|trousers?|pants|sweatpants|joggers?|shorts|skirts?)\b/.test(text)) return 'bottom';
    if (/\b(jackets?|coats?|blazers?|parkas?|puffers?|outerwear|windbreakers?)\b/.test(text)) return 'outer';
    if (/\b(tees?|shirts?|t-shirts?|tops?|henleys?|polos?|vests?|hoodies?|sweatshirts?|sweaters?|cardigans?|knitwear|pullovers?|turtlenecks?)\b/.test(text)) return 'top';
    if (/\b(belts?|necklaces?|bracelets?|rings?|beanies?|hats?|caps?|scarves|scarf|bags?|earrings?|sunglasses|jewel(?:le)?ry)\b/.test(text)) return 'accessory';
    return '';
  };
  return classify(product.title) || classify(product.type) || classify((product.tags || []).join(' '));
}
function authoredLines(product) {
  const doc = new DOMParser().parseFromString(product.description || '', 'text/html');
  doc.querySelectorAll('table,script,style').forEach(node=>node.remove());
  doc.querySelectorAll('br').forEach(node=>node.replaceWith('\n'));
  return unique([...doc.querySelectorAll('p,li')].flatMap(node=>node.textContent.split('\n')).map(clean).filter(Boolean));
}
function designFacts(product, strings) {
  const lines = authoredLines(product);
  const explicit = isSeparate(product) ? '' : lines.filter(line=>/^details\s*:/i.test(line)).join(' ');
  const text = (String(product.title || '') + ' ' + explicit).toLowerCase();
  const piece = isSeparate(product) ? pieceFor(selectionColour(product)) : '';
  const rules = [
    ['asymmetric', /asymmetr.*zip|zip.*asymmetr/, 'construction', 'top'],
    ['croc', /\bcroc(?:odile)?\b/, 'finish'], ['snake', /\bsnake\b/, 'finish'],
    ['waxed', /\bwaxed\b/, 'finish'], ['pleated', /\bpleat(?:ed|s)?\b/, 'construction'],
    ['ribbed', /\bribbed\b/, 'finish'], ['fuzzy', /\bfuzzy\b/, 'finish'],
    ['half_zip', /\bhalf[ -]zip\b/, 'construction', 'top'],
    ['cropped', /\bcropped\b/, 'cut', 'top'], ['flare', /\bflar(?:ed|e)\b/, 'cut', 'bottom'],
    ['wide', /\bwide[ -]leg\b/, 'cut', 'bottom'], ['straight', /\bstraight\b/, 'cut', 'bottom'],
    ['distressed', /\bdistress(?:ed)?\b/, 'finish'], ['layered', /\blayered\b/, 'construction'],
    ['embossed', /\bembossed\b/, 'finish'], ['studs', /\bstud(?:ded|s)\b/, 'construction'],
    ['shoulder_pads', /\bshoulder[ -]pads?\b/, 'construction', 'top'],
    ['metal', /\bmetal\b/, 'construction'], ['pockets', /\bmulti[ -]pocket\b/, 'construction'],
    ['slim', /\b(slim|fitted)\b/, 'cut', 'top']
  ];
  const result = [];
  if (piece) result.push({ label: strings.piece, value: strings[piece] });
  for (const [key, regex, label, onlyPiece] of rules) {
    if (regex.test(text) && (!piece || !onlyPiece || piece === onlyPiece)) result.push({label:strings[label], value:strings[key]});
    if (result.length >= 3) break;
  }
  const material = lines.find(line => /^(?:material|materials|composition|fabric composition)\s*:/i.test(line));
  if (material) {
    const value = material.replace(/^[^:]+:\s*/, '').replace(/[.\s]+$/, '');
    const parts = [...value.matchAll(/(\d+(?:\.\d+)?)\s*%\s*([a-z][a-z\s-]*?)(?=\s*\d|[,;+]|$)/gi)];
    const fibres = parts.map(part => clean(part[2]).toLowerCase());
    const sum = parts.reduce((total, part) => total + Number(part[1]), 0);
    if (value && value.length < 180 && (!parts.length || (Math.abs(sum - 100) <= 1 && new Set(fibres).size === fibres.length))) {
      if (result.length < 3) result.push({label:strings.material, value});
    }
  }
  if (!result.length) result.push({label:strings.design, value:product.title});
  if (result.length < 3 && selectionColour(product)) result.push({label:strings.colour, value:selectionColour(product)});
  return result;
}
const scoreChoice = (current, candidate, colour) => {
  const a = current.title.toLowerCase(), b = candidate.title.toLowerCase();
  let score = 0;
  const currentColour = colourFamily(selectionColour(current)), nextColour = colourFamily(colour.color);
  if (currentColour && currentColour === nextColour) score += 5;
  else if (['black','white','grey'].includes(nextColour)) score += 3;
  else if (currentColour === 'earth' && ['earth','white','black'].includes(nextColour)) score += 3;
  if (/cropped|slim|fitted/.test(a) && /wide|flare|loose/.test(b)) score += 3;
  if (/wide|flare|loose/.test(a) && /cropped|slim|fitted/.test(b)) score += 3;
  const accents = /croc|snake|stud|rhinestone|gem|pearl|emboss|print|metal/;
  if (accents.test(a) && !accents.test(b)) score += 2;
  const significant = text => text.replace(/\b(jacket|pants|trousers|jeans|top|bottom|set|separates|hoodie|sweatpants|shirt|tee|black|white|loose|fit|cropped)\b/g,' ').split(/\W+/).filter(word => word.length > 3);
  const overlap = significant(a).filter(word => significant(b).includes(word)).length;
  if (overlap >= 2) score += Math.min(8, overlap * 3);
  if (String(candidate.id) === String(current.id) && isSeparate(current) && pieceFor(colour.color) !== pieceFor(selectionColour(current))) score += 50;
  return score;
};
function outfitChoices(current, products, slots, previous = [], locked = new Set(), changedSlot = null, turn = 0) {
  const currentRole = category(current);
  const used = new Set([String(current.id) + ':' + (isSeparate(current) ? currentRole : '')]);
  const result = [];
  const old = slot => previous.find(choice => choice.slot === slot);
  for (const slot of slots) {
    const choice = old(slot);
    if (choice && (locked.has(slot) || (changedSlot && slot !== changedSlot))) {
      result.push(choice); used.add(choice.key);
    }
  }
  for (const slot of slots) {
    if (result.some(choice => choice.slot === slot)) continue;
    const candidates = [];
    for (const product of products) {
      if (!product.available) continue;
      for (const colour of product.colors || []) {
        if (!colour.id || !safeImage(colour.image) || !safeURL(product.url)) continue;
        const role = category(product, colour.color);
        if (role !== slot) continue;
        const key = String(product.id) + ':' + (isSeparate(product) ? role : '');
        if (used.has(key)) continue;
        candidates.push({slot,product,colour,key,score:scoreChoice(current,product,colour)});
      }
    }
    const different = candidates.filter(choice => choice.key !== old(slot)?.key);
    const pool = (turn > 0 && different.length ? different : candidates).sort((a,b) => b.score - a.score || a.key.localeCompare(b.key));
    const shortlist = pool.filter(choice => choice.score >= (pool[0]?.score || 0) - 4);
    const bestByProduct = new Map();
    for (const choice of shortlist) if (!bestByProduct.has(choice.key)) bestByProduct.set(choice.key,choice);
    const byProduct = [...bestByProduct.values()];
    const choice = byProduct.length ? byProduct[turn % byProduct.length] : null;
    if (choice) { result.push(choice); used.add(choice.key); }
  }
  return slots.map(slot => result.find(choice => choice.slot === slot)).filter(Boolean);
}

class ACPDP extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.product = parse(this,'[data-acp-product]');
    this.strings = parse(this,'[data-acp-strings]');
    if (!this.product || !this.strings) return;
    this.mode = this.dataset.mode;
    this.unit = 'metric';
    this.choices = [];
    this.locked = new Set();
    this.turn = 0;
    this.guideSignature = '';
    this.frame = 0;
    const signal = this.controller.signal;
    this.addEventListener('click',event => this.onClick(event),{signal});
    document.addEventListener('variant:update',event => this.onVariant(event),{signal});
    document.addEventListener('ac:size-profile-change',()=>this.schedule(),{signal});
    if (this.mode === 'silhouette') {
      customElements.whenDefined('ac-size-suggestion').then(() => { if (this.isConnected) this.schedule(); });
      this.findGuide();
      const main = this.guide?.closest('.shopify-section');
      if (main) {
        this.mainObserver = new MutationObserver(() => this.schedule());
        this.mainObserver.observe(main,{childList:true,subtree:true});
      }
    }
    if (this.mode === 'statement') {
      this.settings = parse(this,'[data-acp-settings]') || {};
      this.settings.count = Number(this.settings.count) === 4 ? 4 : 3;
      this.lazy = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { this.lazy.disconnect(); this.loadOutfit(); }
      },{rootMargin:'400px'});
      this.lazy.observe(this);
    }
    const dialog = this.querySelector('[data-acp-dialog]');
    dialog?.addEventListener('close',()=>this.querySelector('[data-acp-zoom]')?.focus({preventScroll:true}),{signal});
    this.render();
  }

  schedule() {
    if (this.frame || !this.isConnected) return;
    this.frame = requestAnimationFrame(() => { this.frame = 0; if (this.isConnected) this.render(); });
  }

  findGuide() {
    const guide = [...document.querySelectorAll('ac-size-suggestion')].find(node => {
      try { const config = node.config || JSON.parse(node.dataset.acConfig || '{}'); return String(config.product?.id) === String(this.product.id); } catch { return false; }
    });
    if (guide !== this.guide) {
      this.guideObserver?.disconnect();
      this.guide = guide;
      if (guide) {
        this.guideObserver = new MutationObserver(() => this.schedule());
        this.guideObserver.observe(guide,{childList:true,subtree:true,characterData:true,attributes:true,attributeFilter:['data-ac-config','aria-pressed','hidden']});
      }
    }
    return guide;
  }

  mirrorGuide() {
    const slot = this.querySelector('[data-acp-guide-slot]');
    if (!slot) return;
    const guide = this.findGuide();
    const owner = guide?.closest('dialog-component.popup-link');
    const source = owner?.querySelector(':scope > button[data-size-guide-link]');
    this.guideOwner = owner;
    slot.hidden = !source;
    if (!source) return;
    const signature = source.outerHTML;
    if (signature === this.guideSignature) return;
    const focused = slot.contains(document.activeElement);
    const mirror = source.cloneNode(true);
    for (const node of [mirror,...mirror.querySelectorAll('*')]) {
      for (const attr of [...node.attributes]) if (['id','ref'].includes(attr.name) || attr.name.startsWith('on:') || /^on[a-z]+$/.test(attr.name)) node.removeAttribute(attr.name);
    }
    mirror.type = 'button'; mirror.dataset.acpGuide = ''; mirror.setAttribute('aria-haspopup','dialog');
    slot.replaceChildren(mirror); this.guideSignature = signature;
    if (focused) mirror.focus({preventScroll:true});
  }

  onVariant(event) {
    const picker = event.target.closest?.('variant-picker');
    if (!picker || picker.closest('product-card,quick-add-dialog') || String(picker.dataset.productId) !== String(this.product.id)) return;
    const html = event.detail?.data?.html;
    const host = [...(html?.querySelectorAll('ac-pdp') || [])].find(node => node.dataset.sectionId === this.dataset.sectionId);
    const next = host && parse(host,'[data-acp-product]');
    if (!next || String(next.id) !== String(this.product.id)) return;
    const previousColour = selectionColour(this.product);
    this.product = next;
    if (this.mode === 'statement' && this.products && previousColour !== selectionColour(next)) this.remix(null,false);
    this.schedule();
  }

  onClick(event) {
    const button = event.target.closest('button');
    if (!button || !this.contains(button)) return;
    if (button.hasAttribute('data-acp-guide')) {
      const owner = this.guideOwner;
      if (typeof owner?.showDialog === 'function') {
        owner.showDialog();
        owner.querySelector('dialog')?.addEventListener('close',()=>button.isConnected && button.focus({preventScroll:true}),{once:true,signal:this.controller.signal});
      }
    }
    if (button.dataset.acpUnit) {
      this.unit = button.dataset.acpUnit;
      if (this.guide?.setUnits && this.guide.unit !== this.unit) this.guide.setUnits(this.unit);
      this.renderSilhouette();
    }
    if (button.hasAttribute('data-acp-zoom')) {
      const dialog = this.querySelector('[data-acp-dialog]');
      const image = this.querySelector('[data-acp-zoom-image]');
      if (image && safeImage(this.product.selected?.image || this.product.image)) image.src = safeImage(this.product.selected?.image || this.product.image);
      dialog?.showModal();
    }
    if (button.hasAttribute('data-acp-close')) this.querySelector('[data-acp-dialog]')?.close();
    if (button.hasAttribute('data-acp-remix')) this.remix();
    if (button.dataset.acpChange) this.remix(button.dataset.acpChange);
    if (button.dataset.acpLock) {
      const slot = button.dataset.acpLock;
      if (this.locked.has(slot)) this.locked.delete(slot); else this.locked.add(slot);
      this.renderOutfit(); this.querySelector(`[data-acp-lock="${slot}"]`)?.focus({preventScroll:true});
    }
    if (button.hasAttribute('data-acp-retry')) this.loadOutfit();
  }

  render() {
    if (this.mode === 'details') this.renderDetails();
    if (this.mode === 'silhouette') { this.mirrorGuide(); this.renderSilhouette(); }
    if (this.mode === 'statement') this.renderCurrent();
  }

  setImage(image, src, title) {
    const safe = safeImage(src);
    if (!image || !safe) return;
    if (image.getAttribute('src') !== safe) { image.removeAttribute('srcset'); image.src = safe; }
    image.alt = title || this.product.title;
  }

  renderDetails() {
    this.querySelector('.ac-pdp__visual').hidden = !safeImage(this.product.selected?.image || this.product.image);
    this.setImage(this.querySelector('[data-acp-photo] img'),this.product.selected?.image || this.product.image,this.product.title);
    const facts = this.querySelector('[data-acp-facts]');
    const signature = JSON.stringify(designFacts(this.product,this.strings));
    if (signature !== this.factsSignature) {
      const fragment = document.createDocumentFragment();
      JSON.parse(signature).forEach((fact,index) => {
        const row = document.createElement('div'), label = document.createElement('dt'), value = document.createElement('dd');
        row.dataset.number = String(index + 1).padStart(2,'0'); label.textContent = fact.label; value.textContent = fact.value;
        row.append(label,value); fragment.append(row);
      });
      facts.replaceChildren(fragment); this.factsSignature = signature;
    }
    this.querySelector('[data-acp-zoom]').hidden = !safeImage(this.product.selected?.image || this.product.image) || !this.querySelector('dialog')?.showModal;
  }

  renderSilhouette() {
    const s = this.strings, guide = this.guide, analysis = guide?.analysis;
    const separate = isSeparate(this.product), role = category(this.product);
    const labels = [];
    const title = this.product.title.toLowerCase();
    if (role === 'bottom') {
      if (/flar(?:e|ed)/.test(title)) labels.push(s.fit_flared);
      else if (/wide[ -]leg/.test(title)) labels.push(s.fit_wide);
      else if (/straight/.test(title)) labels.push(s.fit_straight);
    } else if (['top','outer'].includes(role)) {
      if (/\b(slim|fitted|skinny)\b/.test(title)) labels.push(s.fit_slim);
      else if (/\b(oversized|oversize|loose)\b/.test(title)) labels.push(s.fit_oversized);
      if (/\bcropped\b/.test(title)) labels.push(s.fit_cropped);
    }
    this.querySelector('[data-acp-fit-line]').textContent = labels.join(' · ');
    this.querySelector('[data-acp-fit-explanation]').hidden = !labels.length;
    this.querySelector('[data-acp-fit-copy]').textContent = role === 'accessory' ? s.accessory_note : !role ? s.unsupported_notice : (separate ? s.separates_note + ' ' : '') + (role === 'bottom' ? s.bottom_note : s.top_note);
    const size = this.product.sizeIndex >= 0 ? this.product.selected?.options?.[this.product.sizeIndex] : '';
    const piece = separate ? pieceFor(selectionColour(this.product)) : '';
    this.querySelector('[data-acp-size]').textContent = [piece ? s[piece] : '',size].filter(Boolean).join(' / ') || s.chart_label;
    if (guide?.unit) this.unit = guide.unit;
    for (const button of this.querySelectorAll('[data-acp-unit]')) button.setAttribute('aria-pressed',String(button.dataset.acpUnit === this.unit));
    const measurements = this.querySelector('[data-acp-measurements]');
    const notes = this.querySelector('[data-acp-measurement-note]');
    const warning = this.querySelector('[data-acp-warning]');
    const source = this.querySelector('[data-acp-source]');
    const chart = !separate && analysis?.supported ? analysis.chart : null;
    const row = chart?.rows?.find(item => clean(item.label).toUpperCase() === clean(size).toUpperCase());
    const fragment = document.createDocumentFragment();
    let note = s.missing_chart, warnings = [];
    if (row) {
      const keys = role === 'bottom' ? ['waist','hip','length'] : ['chest','length','shoulder','sleeve'];
      const shown = keys.filter(key => row[key] && Number.isFinite(row[key].min)).slice(0,3);
      for (const key of shown) {
        const group = document.createElement('div'), label = document.createElement('dt'), value = document.createElement('dd'), unit = document.createElement('small');
        const measure = row[key], factor = this.unit === 'imperial' ? 1 / 2.54 : 1;
        const format = number => new Intl.NumberFormat(document.documentElement.lang || 'en',{maximumFractionDigits:1}).format(number * factor);
        value.append(document.createTextNode(measure.min === measure.max ? format(measure.min) : `${format(measure.min)}–${format(measure.max)}`));
        unit.textContent = this.unit === 'imperial' ? s.inch : s.cm; value.append(unit); label.textContent = s[key]; group.append(label,value); fragment.append(group);
      }
      note = s.circumference;
      if (chart.mode === 'inferred_flat') warnings.push(s.inferred);
      if (chart.ranged) warnings.push(s.range);
      if (chart.sizeLabelMismatch) warnings.push(s.chart_mismatch);
      if (chart.partial) warnings.push(s.partial_chart);
    } else if (chart) note = s.no_row;
    else if (role === 'accessory') {
      const dimension = authoredLines(this.product).find(text=>/^(?:dimensions?|measurements?|length|size)\s*:/i.test(text) && /\d/.test(text) && !/model/i.test(text));
      note = dimension || s.unsupported_notice;
    } else if (!guide) note = s.guide_missing;
    measurements.replaceChildren(fragment);
    notes.textContent = note;
    warning.textContent = warnings.join(' '); warning.hidden = !warnings.length;
    source.hidden = !row;
    this.querySelector('.ac-pdp__units').hidden = !row;
  }

  renderCurrent() {
    this.setImage(this.querySelector('[data-acp-current-photo] img'),this.product.selected?.image || this.product.image,this.product.title);
    this.querySelector('[data-acp-current-colour]').textContent = selectionColour(this.product);
    this.querySelector('[data-acp-current-price]').textContent = this.product.selected?.price || '';
  }

  slots() {
    const role = category(this.product);
    const count = this.settings.count - 1;
    const roles = role === 'bottom' ? ['top','accessory','outer'] : role === 'outer' ? ['bottom','accessory','top'] : role === 'accessory' ? ['top','bottom','outer'] : ['bottom','accessory','outer'];
    return roles.slice(0,count);
  }

  searchURL(query) {
    const url = safeURL(this.settings.search);
    if (!url) return null;
    url.searchParams.set('q',query); url.searchParams.set('type','product'); url.searchParams.set('options[unavailable_products]','hide');
    return url;
  }

  async fetchPage(value, signal) {
    const url = safeURL(value);
    if (!url) throw new Error('Invalid source');
    url.searchParams.set('section_id','ac-pdp-feed');
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id',preview);
    const response = await fetch(url,{signal,credentials:'same-origin',headers:{Accept:'text/html'}});
    if (!response.ok) throw new Error('Feed unavailable');
    const doc = new DOMParser().parseFromString(await response.text(),'text/html');
    const result = parse(doc,'[data-acp-feed]');
    if (!Array.isArray(result?.products)) throw new Error('Feed unavailable');
    return result;
  }

  async loadOutfit() {
    if (this.loading) return;
    this.loading = true;
    this.loadController?.abort(); this.loadController = new AbortController();
    const signal = this.loadController.signal;
    const timer = setTimeout(()=>this.loadController.abort(),18000);
    this.querySelector('[data-acp-retry]').hidden = true;
    this.setStatus(this.strings.loading);
    const sources = unique((this.settings.sources || []).filter(Boolean));
    const accessory = this.searchURL('product_type:Belts OR product_type:Jewelry OR title:necklace OR title:beanie OR title:belt');
    const tops = this.searchURL('title:tee OR title:shirt OR title:sweater OR title:hoodie OR title:top');
    const requests = [...sources.slice(0,3),accessory?.href,tops?.href].filter(Boolean);
    if (!requests.length && this.settings.all) requests.push(this.settings.all);
    try {
      const outcomes = await Promise.allSettled(requests.map(url=>this.fetchPage(url,signal)));
      if (!this.isConnected) return;
      if (signal.aborted) throw new Error('Request timed out');
      const pages = outcomes.filter(result=>result.status==='fulfilled').map(result=>result.value);
      if (!pages.length) throw new Error('No source available');
      let products = pages.flatMap(page=>page.products);
      if (isSeparate(this.product) && this.product.colors?.length) products.push(this.product);
      const roles = this.slots();
      const enough = () => roles.every(role=>products.filter(product=>(product.colors || []).some(colour=>category(product,colour.color)===role)).length>=3);
      if (!enough()) {
        const more = unique(pages.map(page=>page.next).filter(Boolean)).slice(0,3);
        const extra = await Promise.allSettled(more.map(url=>this.fetchPage(url,signal)));
        products.push(...extra.filter(result=>result.status==='fulfilled').flatMap(result=>result.value.products));
      }
      if (!this.isConnected) return;
      if (signal.aborted) throw new Error('Request timed out');
      const byId = new Map();
      for (const product of products) {
        const old = byId.get(String(product.id));
        if (!old) byId.set(String(product.id),product);
        else old.colors = [...new Map([...(old.colors || []),...(product.colors || [])].map(colour=>[colour.color,colour])).values()];
      }
      this.products = [...byId.values()];
      this.choices = outfitChoices(this.product,this.products,this.slots(),this.choices,this.locked,null,this.turn);
      this.renderOutfit();
      this.setStatus(this.choices.length ? (isSeparate(this.product) ? this.strings.set_hint : '') : this.strings.empty);
    } catch {
      if (this.isConnected) { this.querySelector('[data-acp-retry]').hidden = false; this.setStatus(this.strings.error); this.querySelectorAll('[data-acp-placeholder]').forEach(node=>node.remove()); }
    } finally {
      clearTimeout(timer); this.loading = false;
      this.querySelector('[data-acp-outfit]')?.setAttribute('aria-busy','false');
    }
  }

  remix(slot = null, announce = true) {
    if (!this.products) return;
    if (slot && this.locked.has(slot)) return;
    if (this.choices.length && this.choices.every(choice=>this.locked.has(choice.slot))) { this.setStatus(this.strings.all_locked); return; }
    const previous = this.choices;
    this.turn += 1;
    this.choices = outfitChoices(this.product,this.products,this.slots(),previous,this.locked,slot,this.turn);
    const changed = this.choices.some(choice=>previous.find(old=>old.slot===choice.slot)?.key !== choice.key);
    const focused = document.activeElement?.dataset.acpChange;
    this.renderOutfit();
    if (focused) this.querySelector(`[data-acp-change="${focused}"]`)?.focus({preventScroll:true});
    if (announce) this.setStatus(changed ? this.strings.updated : this.strings.no_alternative);
  }

  renderOutfit() {
    const root = this.querySelector('[data-acp-outfit]'), s = this.strings;
    root.querySelectorAll('[data-acp-choice],[data-acp-placeholder]').forEach(node=>node.remove());
    for (const choice of this.choices) {
      const locked = this.locked.has(choice.slot);
      const card = document.createElement('article'); card.className='ac-pdp__card'; card.dataset.acpChoice=choice.slot; card.dataset.locked=String(locked);
      const top = document.createElement('div');top.className='ac-pdp__card-top';
      const role = document.createElement('p');role.className='ac-pdp__role';role.textContent=s[choice.slot] || s.piece;
      const lock = document.createElement('button');lock.type='button';lock.className='ac-pdp__lock';lock.dataset.acpLock=choice.slot;lock.setAttribute('aria-pressed',String(locked));lock.setAttribute('aria-label',`${locked?s.unlock:s.lock}: ${choice.product.title}`);
      lock.innerHTML=locked ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="5" y="10" width="14" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3m-4 5v2"/></svg>' : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="5" y="10" width="14" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 7.5-2m-3.5 10v2"/></svg>';
      top.append(role,lock);
      const url=safeURL(choice.product.url);url.searchParams.set('variant',choice.colour.id);
      const link=document.createElement('a');link.href=url.href;link.className='ac-pdp__card-photo';link.setAttribute('aria-label',choice.product.title);
      const image=document.createElement('img');image.src=safeImage(choice.colour.image);image.width=800;image.height=1000;image.loading='lazy';image.decoding='async';image.alt=choice.product.title;link.append(image);
      const copy=document.createElement('div');copy.className='ac-pdp__card-copy';
      const title=document.createElement('h3'), titleLink=document.createElement('a');titleLink.href=url.href;titleLink.textContent=choice.product.title;title.append(titleLink);
      const colour=document.createElement('p');colour.className='ac-pdp__card-colour';colour.textContent=choice.colour.color;
      const price=document.createElement('p');price.className='ac-pdp__price';price.textContent=choice.colour.price;
      const actions=document.createElement('div');actions.className='ac-pdp__card-actions';
      const view=document.createElement('a');view.href=url.href;view.textContent=s.view;view.className='ac-pdp__text-button';
      const change=document.createElement('button');change.type='button';change.className='ac-pdp__change';change.dataset.acpChange=choice.slot;change.disabled=locked;change.textContent=locked?s.locked:s.change;change.setAttribute('aria-label',`${locked?s.locked:s.change}: ${choice.product.title}`);
      actions.append(view,change);copy.append(title,colour,price,actions);card.append(top,link,copy);root.append(card);
    }
    root.dataset.count=String(this.choices.length+1);root.setAttribute('aria-busy','false');
    this.querySelector('[data-acp-remix]').disabled=!this.choices.length;
    this.renderCurrent();
  }

  setStatus(value) { const node=this.querySelector('[data-acp-status]');if(node) node.textContent=value || ''; }
  disconnectedCallback() {
    this.controller?.abort();this.loadController?.abort();this.lazy?.disconnect();this.guideObserver?.disconnect();this.mainObserver?.disconnect();cancelAnimationFrame(this.frame);
    this.controller=null;this.guide=null;this.loading=false;
  }
}
if (!customElements.get('ac-pdp')) customElements.define('ac-pdp',ACPDP);
export { category, isSeparate, designFacts, outfitChoices, scoreChoice };
